import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import dashboardData from "../data/private/dashboard.json";
import VastuCompassPlaceholder from "../components/VastuCompassPlaceholder";
import activityData from "../data/private/activity.json";
import { getActivities, getProgress, logActivity } from "../utils/activity";
import ProgressTracker from "../components/ProgressTracker";
import OnboardingTour from "../components/OnboardingTour";
import MetricsCard from "../components/MetricsCard";
import ChartWidget from "../components/ChartWidget";
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import LoadingState from "../components/ui/LoadingState";
import KeyValueTable from "../components/ui/KeyValueTable";
 
import { useDashboard } from "../context/DashboardContext";

export default function DashboardHome() {
  const [activities] = useState(getActivities());
  const [progressMap] = useState(getProgress());
  const { metrics, nextActions, loadingMetrics, loadingNextActions, error, fetchMetrics, fetchNextActions } =
    useDashboard();
  const actionItems = Array.isArray(nextActions) ? nextActions : [];

  const handleChartHover = (chartId) => (payload) => {
    const activePayload = payload?.activePayload?.[0];
    if (!activePayload) return;
    logActivity("chart_hovered", {
      chart: chartId,
      label: activePayload.payload?.name,
      value: activePayload.value,
    });
  };

  useEffect(() => {
    logActivity("dashboard_loaded");
  }, []);

  useEffect(() => {
    fetchMetrics().catch(() => {});
    fetchNextActions().catch(() => {});
  }, [fetchMetrics, fetchNextActions]);

  const metricsCards = useMemo(() => {
    const config = [
      { key: "totalClients", label: "Total Clients" },
      { key: "totalProperties", label: "Total Properties" },
      { key: "activeAnalyses", label: "Active Analyses" },
      { key: "completedReports", label: "Completed Reports" },
    ];
    return config.map((card) => ({
      id: card.key,
      label: card.label,
      value: metrics?.[card.key] ?? "",
      status: "neutral",
    }));
  }, [metrics]);

  const progressMetrics = useMemo(() => {
    const progress = metrics?.progress || {};
    return [
      { key: "clients", label: "Progress (clients)", value: progress.clients },
      { key: "reports", label: "Progress (reports)", value: progress.reports },
      { key: "analyses", label: "Progress (analyses)", value: progress.analyses },
      { key: "properties", label: "Progress (properties)", value: progress.properties },
    ];
  }, [metrics?.progress]);

  const sectionMotion = {
    initial: { opacity: 0, y: 6 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.2 },
  };

  return (
    <div className="space-y-8">
      <OnboardingTour />

      <motion.section {...sectionMotion}>
        <h1 className="text-2xl font-semibold text-slate-900">
          {dashboardData.overview.title}
        </h1>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          {dashboardData.overview.cards.map((card) => (
            <div key={card.title} className="card card-hover p-4">
              <p className="text-sm font-semibold text-slate-900">
                {card.title}
              </p>
              <p className="mt-2 text-sm text-slate-600">{card.description}</p>
            </div>
          ))}
        </div>
      </motion.section>

      <motion.section {...sectionMotion}>
        <h2 className="text-lg font-semibold text-slate-900">
          Dashboard metrics
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Snapshot of your workflow.
        </p>
        {loadingMetrics ? (
          <LoadingState variant="cards" rows={4} />
        ) : metricsCards.length ? (
          <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {metricsCards.map((metric) => (
              <MetricsCard
                key={metric.id}
                metric={metric}
                onClick={(item) =>
                  logActivity("metrics_card_clicked", { id: item.id })
                }
              />
            ))}
          </div>
        ) : (
          <div className="mt-4">
            {error ? (
              <ErrorState error={error} />
            ) : (
              <EmptyState
                title="No metrics yet"
                message="Metrics will appear once activity is recorded."
                testId="dashboard-empty-metrics"
              />
            )}
          </div>
        )}
        {!loadingMetrics && (
          <div className="mt-4 grid gap-2 text-xs text-slate-600">
            {progressMetrics.map((item) => (
              <div key={item.key} className="card p-3">
                <span className="font-semibold text-slate-900">
                  {item.label}
                </span>
                <span className="ml-2">{item.value ?? "—"}</span>
              </div>
            ))}
          </div>
        )}
      </motion.section>

      <motion.section {...sectionMotion}>
        <h2 className="text-lg font-semibold text-slate-900">
          {dashboardData.workspace.title}
        </h2>
        <div className="mt-4 card card-hover p-6">
          <VastuCompassPlaceholder />
        </div>
      </motion.section>

      <motion.section {...sectionMotion}>
        <h2 className="text-lg font-semibold text-slate-900">
          {dashboardData.progress.title}
        </h2>
        <div className="mt-4 card p-6">
          <ProgressTracker
            steps={dashboardData.progress.steps}
            completedMap={progressMap}
          />
        </div>
      </motion.section>

      <motion.section {...sectionMotion}>
        <h2 className="text-lg font-semibold text-slate-900">
          {activityData.title}
        </h2>
        <div className="mt-4 space-y-3">
          {activities.length === 0 ? (
            <EmptyState
              title="No activity yet"
              message="Recent activity will appear here."
              testId="dashboard-empty-activity"
            />
          ) : (
            activities.map((activity, index) => (
              <div
                key={`${activity.timestamp || "activity"}-${index}`}
                className="card card-hover p-4"
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg" aria-hidden="true">
                    {activity.icon ||
                      activityData.items?.find((item) => item.type === activity.type)
                        ?.icon ||
                      "📌"}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {activity.title || activity.label}
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(activity.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </motion.section>

      <motion.section {...sectionMotion}>
        <h2 className="text-lg font-semibold text-slate-900">Insights</h2>
        <p className="mt-1 text-sm text-slate-600">
          Insights are not available yet.
        </p>
        <div className="mt-4">
          <EmptyState
            title="Insights unavailable"
            message="Insights will appear once data is available."
            testId="dashboard-empty-insights"
          />
        </div>
      </motion.section>

      <motion.section {...sectionMotion}>
        <h2 className="text-lg font-semibold text-slate-900">Next actions</h2>
        <p className="mt-1 text-sm text-slate-600">
          Tasks generated by the backend.
        </p>
        {actionItems.length === 0 ? (
          <div className="mt-4">
            <EmptyState
              title="No next actions yet"
              message="Backend-generated tasks will appear here."
              testId="dashboard-empty-actions"
            />
          </div>
        ) : (
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {actionItems.map((action, index) => (
              <div key={`next-action-${index}`} className="card p-4">
                <p className="text-sm font-semibold text-slate-900">
                  Next action
                </p>
                {typeof action === "string" ? (
                  <p className="mt-2 text-sm text-slate-600">{action}</p>
                ) : (
                  <div className="mt-2">
                    <KeyValueTable data={action} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </motion.section>

      <motion.section {...sectionMotion}>
        <div className="grid gap-4 md:grid-cols-2">
          <ChartWidget
            title="Remedies"
            subtitle="Chart data unavailable"
            config={{ type: "bar", nameKey: "name", valueKey: "value" }}
            data={[]}
            onHover={handleChartHover("remedies")}
          />
          <ChartWidget
            title="Analyses"
            subtitle="Chart data unavailable"
            config={{ type: "bar", nameKey: "name", valueKey: "value" }}
            data={[]}
            onHover={handleChartHover("analyses")}
          />
        </div>
      </motion.section>

      <motion.section {...sectionMotion}>
        <h2 className="text-lg font-semibold text-slate-900">
          {dashboardData.analyses.title}
        </h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="card card-hover p-4">
            <p className="text-sm font-semibold text-slate-900">
              {dashboardData.analyses.title}
            </p>
            <p className="mt-2 text-xs text-slate-500">
              {dashboardData.analyses.ctaLabel}
            </p>
          </div>
        </div>
      </motion.section>
    </div>
  );
}
